import { useState, useEffect, type ReactNode } from 'react';
import { useParams } from 'react-router-dom';
import { useProduct, useProducts } from '../hooks/useCatalog';
import { trackRecentlyViewed } from '../lib/recentlyViewed';
import { useAddCartItem, useOptionalServices } from '../hooks/useCart';
import { useCurrency } from '../hooks/useCurrency';
import { useSales } from '../hooks/useSales';
import type { Product } from '../types';
import { Container } from '../components/ui/Container';
import { Eyebrow } from '../components/ui/Eyebrow';
import { EditorialImage } from '../components/ui/EditorialImage';
import { ProductCard } from '../components/ProductCard';
import { RatingStars } from '../components/ui/RatingStars';
import { SizeGuideButton } from '../components/SizeGuideModal';

export function ProductPage() {
  const { slug = '' } = useParams();
  const { data: product, isLoading, isError } = useProduct(slug);
  const { data: allProducts } = useProducts();
  const { data: sales } = useSales();
  const addCartItem = useAddCartItem();
  const { format } = useCurrency();

  const { data: optionalServices } = useOptionalServices();

  const [sizeId, setSizeId] = useState<number | null>(null);
  const [frameId, setFrameId] = useState<number | null>(null);
  const [materialId, setMaterialId] = useState<number | null>(null);
  const [selectedAttrOptions] = useState<Record<string, number>>({});
  const [selectedServiceIds, setSelectedServiceIds] = useState<number[]>([]);
  const [quantity, setQuantity] = useState(1);
  const [showValidation, setShowValidation] = useState(false);

  useEffect(() => {
    if (product?.id) trackRecentlyViewed(product.id);
  }, [product?.id]);

  useEffect(() => {
    if (product?.sizes?.length && sizeId === null) {
      const firstInStock = product.sizes.find(s => {
        const isDisabled = s.is_active === false || (s.is_enabled != null && !s.is_enabled);
        return !isDisabled && (s.stock == null || Number(s.stock) > 0);
      }) ?? product.sizes[0];
      setSizeId(firstInStock.id);
    }
  }, [product?.id]);

  if (isLoading) {
    return (
      <Container className="py-24">
        <div className="grid gap-12 md:grid-cols-2">
          <div className="aspect-[4/5] animate-pulse bg-stone-light/50" />
          <div className="space-y-4 pt-4">
            <div className="h-4 w-24 animate-pulse bg-stone-light/50" />
            <div className="h-10 w-2/3 animate-pulse bg-stone-light/50" />
            <div className="h-4 w-1/3 animate-pulse bg-stone-light/50" />
          </div>
        </div>
      </Container>
    );
  }

  if (isError || !product) {
    return (
      <Container className="py-32 text-center">
        <Eyebrow>Not Found</Eyebrow>
        <p className="text-balance mx-auto mt-6 font-serif text-3xl font-light text-ink">
          This piece isn't available yet — it may still be on press.
        </p>
      </Container>
    );
  }

  const matchingVariant = product.variants?.find(
    (variant) =>
      (variant.size_id ?? null) === sizeId &&
      (variant.frame_id ?? null) === frameId &&
      (variant.material_id ?? null) === materialId,
  );

  const hasSizes     = !!product.sizes?.length;
  const hasFrames    = !!product.frames?.length;
  const hasMaterials = !!product.materials?.length;
  const hasVariants  = !!product.variants?.length;

  const missingSizeId     = hasSizes && sizeId === null;
  const missingFrameId    = hasFrames && frameId === null;
  const missingMaterialId = hasMaterials && materialId === null;
  const missingVariant    = hasVariants && !matchingVariant;

  const selectedSize = hasSizes ? product.sizes!.find(s => s.id === sizeId) : undefined;
  const selectedSizeOutOfStock = selectedSize != null && selectedSize.stock != null && Number(selectedSize.stock) === 0;

  const canAddToCart = !missingSizeId && !missingFrameId && !missingMaterialId && !missingVariant && !selectedSizeOutOfStock;

  const handleAddToCart = () => {
    if (!canAddToCart) {
      setShowValidation(true);
      return;
    }
    addCartItem.mutate({
      product_id: product.id,
      product_variant_id: matchingVariant?.id ?? null,
      size_id: sizeId,
      frame_id: frameId,
      material_id: materialId,
      optional_service_ids: selectedServiceIds.length ? selectedServiceIds : undefined,
      quantity,
    });
  };

  // Compute display price from selected options
  let computedPrice = Number(product.base_price);
  let hasOptionPrice = false;

  if (matchingVariant?.price_override != null) {
    computedPrice = Number(matchingVariant.price_override);
    hasOptionPrice = true;
  } else {
    if (sizeId !== null) {
      const sz = product.sizes?.find((s) => s.id === sizeId);
      // If product has no base price, use the size's global extra_price as the base
      if (computedPrice === 0 && sz?.extra_price != null && Number(sz.extra_price) !== 0) {
        computedPrice = Number(sz.extra_price);
        hasOptionPrice = true;
      } else {
        const mod = sz?.price_modifier != null && sz.price_modifier !== '' ? Number(sz.price_modifier) : 0;
        if (mod !== 0) {
          computedPrice += mod;
          hasOptionPrice = true;
        }
      }
    }
    if (frameId !== null) {
      const fr = product.frames?.find((f) => f.id === frameId);
      if (fr?.price_override != null && Number(fr.price_override) !== 0) {
        computedPrice += Number(fr.price_override);
        hasOptionPrice = true;
      }
    }
    if (materialId !== null) {
      const mat = product.materials?.find((m) => m.id === materialId);
      if (mat?.price_override != null && Number(mat.price_override) !== 0) {
        computedPrice += Number(mat.price_override);
        hasOptionPrice = true;
      }
    }
  }

  // Add extra_price from selected attribute options
  for (const optId of Object.values(selectedAttrOptions)) {
    const opt = product.attribute_options?.find((o) => o.id === optId);
    if (opt?.extra_price != null) {
      computedPrice += Number(opt.extra_price);
    }
  }

  // Apply promotion only when no option-specific price is in effect
  const promotionActive = !hasOptionPrice && product.is_on_sale && product.discounted_price != null;
  const price = promotionActive ? product.discounted_price! : computedPrice;
  const originalPrice = promotionActive ? (product.original_price ?? Number(product.base_price)) : null;

  const applicableSale = sales?.find(
    (sale) =>
      sale.type === 'buy_x_get_y' &&
      ((!sale.product_ids?.length && !sale.category_ids?.length) ||
        sale.product_ids?.includes(product.id) ||
        (product.category_id && sale.category_ids?.includes(product.category_id))),
  );

  const related = (allProducts ?? []).filter((p) => p.slug !== product.slug).slice(0, 4);

  const reviews = product.reviews ?? [];
  const rating = {
    value: reviews.length ? reviews.reduce((sum, r) => sum + r.rating, 0) / reviews.length : 0,
    count: reviews.length,
  };

  return (
    <div>
      <Container className="py-10 md:py-14">
        <div className="grid gap-14 md:grid-cols-2 md:gap-20">
          <div className="space-y-4">
            <div className="relative">
              <EditorialImage
                src={product.images?.[0]?.path}
                seed={`product-${product.slug}`}
                alt={product.name}
                duotone={false}
                aspect="aspect-[4/5]"
                size="large"
                priority
              />
            </div>
            {product.images && product.images.length > 1 && (
              <div className="grid grid-cols-3 gap-4">
                {product.images.slice(1, 4).map((image) => (
                  <EditorialImage
                    key={image.id}
                    src={image.path}
                    seed={`product-${product.slug}-${image.id}`}
                    alt={product.name}
                    duotone={false}
                    size="thumb"
                    aspect="aspect-square"
                  />
                ))}
              </div>
            )}
          </div>

          <div className="md:sticky md:top-24 md:self-start">
            <Eyebrow>Framed Print</Eyebrow>

            {/* Promotion badge */}
            {product.has_active_promotion && product.promotion_badge && (
              <div className="mt-3 inline-flex items-center gap-1.5 bg-clay px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.1em] text-cream">
                {product.promotion_badge}
              </div>
            )}

            <h1 className="text-balance mt-4 font-serif text-4xl font-light leading-tight text-ink md:text-5xl">
              {product.name}
            </h1>

            {/* Price block */}
            <div className="mt-4 flex flex-wrap items-baseline gap-x-3 gap-y-1">
              <span className={`text-2xl font-semibold ${promotionActive ? 'text-clay' : 'text-ink'}`}>
                {format(price)}
              </span>
              {promotionActive && originalPrice != null && (
                <span className="text-base text-stone line-through">{format(originalPrice)}</span>
              )}
              {promotionActive && product.discount_percentage != null && (
                <span className="text-sm font-semibold text-clay">−{product.discount_percentage}%</span>
              )}
            </div>

            {/* Savings callout */}
            {promotionActive && product.discount_amount != null && product.discount_amount > 0 && (
              <p className="mt-1 text-xs font-medium text-moss">
                You save {format(product.discount_amount)}
              </p>
            )}

            {selectedSizeOutOfStock
              ? <p className="mt-2 text-xs font-medium text-clay">Out of stock</p>
              : <p className="mt-2 text-xs font-medium text-moss">In stock</p>
            }

            {applicableSale && (
              <div className="mt-4 border border-moss/30 bg-moss/5 px-4 py-3">
                <p className="text-sm font-medium text-moss">
                  {applicableSale.name}: Buy {applicableSale.buy_quantity} Get {applicableSale.get_quantity} Free
                </p>
                <p className="mt-1 text-xs text-stone">
                  Add {applicableSale.buy_quantity + applicableSale.get_quantity} to your cart and get {applicableSale.get_quantity} free.
                </p>
              </div>
            )}

            {product.description && (
              <p className="mt-6 max-w-md text-sm leading-relaxed text-stone">{product.description}</p>
            )}

            {hasSizes && (
              <OptionGroup label="Size" error={showValidation && missingSizeId ? 'Please select a size' : undefined}>
                {product.sizes!.map((size) => {
                  const disabled = size.is_active === false || (size.is_enabled != null && !size.is_enabled);
                  const outOfStock = disabled || (size.stock != null && Number(size.stock) === 0);
                  return (
                    <OptionPill
                      key={size.id}
                      label={size.name}
                      active={sizeId === size.id}
                      soldOut={outOfStock}
                      onClick={() => { if (!disabled) { setSizeId(size.id); setShowValidation(false); } }}
                    />
                  );
                })}
              </OptionGroup>
            )}

            <SizeGuideButton className="mt-4" />

            {hasFrames && (
              <OptionGroup label="Frame" error={showValidation && missingFrameId ? 'Please select a frame' : undefined}>
                {product.frames!.map((frame) => {
                  const disabled = frame.is_active === false || (frame.is_enabled != null && !frame.is_enabled);
                  return (
                    <OptionPill
                      key={frame.id}
                      label={frame.name}
                      active={frameId === frame.id}
                      soldOut={disabled}
                      onClick={() => { if (!disabled) { setFrameId(frame.id); setShowValidation(false); } }}
                    />
                  );
                })}
              </OptionGroup>
            )}

            {hasMaterials && (
              <OptionGroup label="Material" error={showValidation && missingMaterialId ? 'Please select a material' : undefined}>
                {product.materials!.map((material) => {
                  const disabled = material.is_active === false || (material.is_enabled != null && !material.is_enabled);
                  return (
                    <OptionPill
                      key={material.id}
                      label={material.name}
                      active={materialId === material.id}
                      soldOut={disabled}
                      onClick={() => { if (!disabled) { setMaterialId(material.id); setShowValidation(false); } }}
                    />
                  );
                })}
              </OptionGroup>
            )}


            {optionalServices && optionalServices.length > 0 && (
              <div className="mt-7">
                <p className="text-[11px] font-medium uppercase tracking-[0.16em] text-stone">Add-ons</p>
                <div className="mt-3 space-y-2">
                  {optionalServices.map((service) => {
                    const selected = selectedServiceIds.includes(service.id);
                    return (
                      <label
                        key={service.id}
                        className={`flex cursor-pointer items-center justify-between border px-4 py-3 text-sm transition-colors ${
                          selected ? 'border-ink bg-ink/5' : 'border-stone-light hover:border-ink/30'
                        }`}
                      >
                        <span className="flex items-center gap-3">
                          <input
                            type="checkbox"
                            checked={selected}
                            onChange={() =>
                              setSelectedServiceIds((prev) =>
                                prev.includes(service.id)
                                  ? prev.filter((id) => id !== service.id)
                                  : [...prev, service.id],
                              )
                            }
                            className="h-4 w-4 accent-ink"
                          />
                          <span className="font-medium text-ink">{service.name}</span>
                        </span>
                        <span className="text-stone">+{format(Number(service.extra_price))}</span>
                      </label>
                    );
                  })}
                </div>
              </div>
            )}

            {showValidation && !canAddToCart && (
              <div className="mt-5 border border-clay/40 bg-clay/5 px-4 py-3">
                <p className="text-sm font-medium text-clay">Please select all required options before adding to cart.</p>
              </div>
            )}

            {showValidation && missingVariant && !missingSizeId && !missingFrameId && !missingMaterialId && (
              <div className="mt-5 border border-clay/40 bg-clay/5 px-4 py-3">
                <p className="text-sm font-medium text-clay">This combination is not available. Please try a different selection.</p>
              </div>
            )}

            <div className="mt-6 flex items-stretch gap-3">
              <div className="flex items-center border border-stone-light">
                <button
                  type="button"
                  onClick={() => setQuantity((q) => Math.max(1, q - 1))}
                  className="px-3.5 py-3 text-stone hover:text-ink"
                  aria-label="Decrease quantity"
                >
                  −
                </button>
                <span className="w-8 text-center text-sm">{quantity}</span>
                <button
                  type="button"
                  onClick={() => setQuantity((q) => q + 1)}
                  className="px-3.5 py-3 text-stone hover:text-ink"
                  aria-label="Increase quantity"
                >
                  +
                </button>
              </div>
              <button
                type="button"
                onClick={handleAddToCart}
                disabled={addCartItem.isPending}
                className={`flex-1 px-6 py-3 text-[13px] font-semibold uppercase tracking-[0.14em] text-cream transition-colors disabled:opacity-50 ${
                  canAddToCart ? 'bg-ink hover:bg-clay' : 'bg-stone cursor-not-allowed'
                }`}
              >
                {addCartItem.isPending ? 'Adding…' : selectedSizeOutOfStock ? 'Out of Stock' : canAddToCart ? 'Add to Cart' : 'Select Options'}
              </button>
            </div>
            {addCartItem.isSuccess && (
              <p className="mt-3 text-sm text-moss">Added to your cart.</p>
            )}
            {addCartItem.isError && (
              <p className="mt-3 text-sm text-clay">Couldn't add to cart — please try again.</p>
            )}

            <div className="mt-6 grid grid-cols-3 gap-3 border-y border-stone-light py-5 text-center">
              <TrustChip label="Premium Print" />
              <TrustChip label="Zero Pixelation" />
              <TrustChip label="High Quality Frame" />
            </div>

            <div className="mt-8 space-y-6">
              <DetailRow title="Shipping" copy="Dispatched within 3–5 working days, packed flat and boxed." />
            </div>
          </div>
        </div>
      </Container>

      <ReviewsSection product={product} rating={rating} />

      {related.length > 0 && (
        <section className="border-t border-stone-light py-16 md:py-20">
          <Container>
            <Eyebrow>You May Also Like</Eyebrow>
            <h2 className="mt-3 font-serif text-3xl font-light text-ink md:text-4xl">Complete the room.</h2>
            <div className="mt-10 grid grid-cols-2 gap-x-6 gap-y-10 md:grid-cols-4">
              {related.map((p) => (
                <ProductCard key={p.slug} product={p} />
              ))}
            </div>
          </Container>
        </section>
      )}
    </div>
  );
}

function ReviewsSection({
  product,
  rating,
}: {
  product: Product;
  rating: { value: number; count: number };
}) {
  const dbReviews = product.reviews ?? [];

  return (
    <section className="bg-paper py-16 md:py-20">
      <Container>
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <Eyebrow>Customer Reviews</Eyebrow>
            <h2 className="mt-3 font-serif text-3xl font-light text-ink md:text-4xl">
              {rating.value.toFixed(1)} out of 5
            </h2>
            <div className="mt-2">
              <RatingStars value={rating.value} count={rating.count} />
            </div>
          </div>
        </div>

        {dbReviews.length > 0 ? (
          <div className="mt-10 grid gap-6 md:grid-cols-3">
            {dbReviews.map((review) => (
              <div key={review.id} className="border border-stone-light bg-cream p-6">
                <RatingStars value={review.rating} />
                {review.comment && (
                  <p className="mt-3 text-sm leading-relaxed text-ink-soft">"{review.comment}"</p>
                )}
                <p className="mt-4 text-xs font-medium uppercase tracking-[0.08em] text-stone">
                  {review.author_name} · Verified Buyer
                </p>
              </div>
            ))}
          </div>
        ) : (
          <p className="mt-10 text-sm text-stone">No reviews yet — be the first to leave one.</p>
        )}
      </Container>
    </section>
  );
}

function TrustChip({ label }: { label: string }) {
  return <p className="text-[11px] font-medium uppercase tracking-[0.06em] text-stone">{label}</p>;
}

function OptionGroup({ label, error, children }: { label: string; error?: string; children: ReactNode }) {
  return (
    <div className="mt-7">
      <div className="flex items-center gap-2">
        <p className={`text-[11px] font-medium uppercase tracking-[0.16em] ${error ? 'text-clay' : 'text-stone'}`}>{label}</p>
        {error && <p className="text-[10px] text-clay">{error}</p>}
      </div>
      <div className={`mt-3 flex flex-wrap gap-2 ${error ? 'rounded-sm outline outline-1 outline-clay/30 p-2 -mx-2' : ''}`}>{children}</div>
    </div>
  );
}

function OptionPill({ label, active, soldOut = false, onClick }: { label: string; active: boolean; soldOut?: boolean; onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`relative flex flex-col items-center border px-4 py-2 text-sm transition-colors ${
        soldOut
          ? 'border-stone-light text-stone/40 cursor-not-allowed'
          : active
            ? 'border-ink bg-ink text-cream'
            : 'border-stone-light text-ink-soft hover:border-ink'
      }`}
    >
      {label}
      {soldOut && (
        <span className="mt-0.5 text-[9px] font-medium uppercase tracking-wide text-stone/50">
          Out of stock
        </span>
      )}
    </button>
  );
}

function DetailRow({ title, copy }: { title: string; copy: string }) {
  return (
    <div>
      <p className="text-sm font-medium text-ink">{title}</p>
      <p className="mt-1 text-sm leading-relaxed text-stone">{copy}</p>
    </div>
  );
}
